/**
 * InvoiceViewLineItems
 *
 * Renders the line-items table for an invoice.
 */

import { formatAmount } from '../../../types/billing'

import type { Invoice } from '../../../types/billing'

interface InvoiceViewLineItemsProps {
  invoice: Invoice
  isExport: boolean
}

export function InvoiceViewLineItems({ invoice, isExport }: InvoiceViewLineItemsProps): JSX.Element {
  return (
    <div className="items-section">
      <table className="items-table">
        <LineItemsHead isExport={isExport} />
        <tbody>
          {invoice.items?.map((item, index) => (
            <LineItemRow
              key={item.id ?? index}
              currency={invoice.currency}
              index={index}
              isExport={isExport}
              item={item}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LineItemsHead({ isExport }: { isExport: boolean }): JSX.Element {
  return (
    <thead>
      <tr>
        <th>#</th>
        <th>Description</th>
        <th>HSN/SAC</th>
        <th>Qty</th>
        <th>Unit</th>
        <th>Rate</th>
        {!isExport && <th>Tax</th>}
        <th>Amount</th>
      </tr>
    </thead>
  )
}

interface LineItemRowProps {
  item: NonNullable<Invoice['items']>[number]
  index: number
  isExport: boolean
  currency: string | undefined
}

function computeLineItemGstRate(item: NonNullable<Invoice['items']>[number]): number {
  const cgstRate = parseFloat(String(item.cgstRate)) || 0
  const sgstRate = parseFloat(String(item.sgstRate)) || 0
  const igstRate = parseFloat(String(item.igstRate)) || 0
  return cgstRate + sgstRate + igstRate
}

function computeLineItemTaxAmount(item: NonNullable<Invoice['items']>[number]): number {
  return (Number(item.cgstAmount) || 0) + (Number(item.sgstAmount) || 0) + (Number(item.igstAmount) || 0)
}

function LineItemRow({ item, index, isExport, currency }: LineItemRowProps): JSX.Element {
  const gstRate = computeLineItemGstRate(item)
  const taxAmount = computeLineItemTaxAmount(item)
  const hsnSac = item.hsnCode || item.sacCode || '-'
  const hsnDisplay = hsnSac !== '-' && gstRate > 0 ? `${hsnSac} (${gstRate}%)` : hsnSac

  return (
    <tr>
      <td>{index + 1}</td>
      <td>{item.description}</td>
      <td>{hsnDisplay}</td>
      <td>{parseFloat(String(item.quantity)) || item.quantity}</td>
      <td>{item.unit}</td>
      <td className="amount">{formatAmount(item.unitPrice, currency)}</td>
      {!isExport && <td className="amount">{formatAmount(taxAmount, currency)}</td>}
      <td className="amount">{formatAmount(item.totalAmount, currency)}</td>
    </tr>
  )
}
